/*************************************************
 * PermissionModal.jsx – PRODUCTION READY (GMPRCF)
 *************************************************/

import { useEffect, useState } from "react";
import { apiFetch } from "../utils/apifetch";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import "./PermissionModal.css";

const RESOURCES = [
  "profile",
  "article",
  "judgement",
  "judge",
  "act",
  "user",
  "journal",
];

const ACTIONS = ["read", "create", "update", "delete"];

export default function PermissionModal({ open, onClose, user }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD PERMISSIONS ================= */
  useEffect(() => {
    if (!open || !user) return;

    setLoading(true);
    setError("");

    apiFetch(
      `/users/${user.uid}/permissions`,
      {},
      logout,
      navigate
    )
      .then((res) => res.json())
      .then((data) => {
        const normalized = {};

        Object.entries(data || {}).forEach(([resource, actions]) => {
          const set = new Set(actions);

          // 🔐 HARD ENFORCEMENT: write implies read
          if (
            set.has("create") ||
            set.has("update") ||
            set.has("delete")
          ) {
            set.add("read");
          }

          normalized[resource] = Array.from(set);
        });

        setPermissions(normalized);
      })
      .catch((err) =>
        setError(err.message || "Failed to load permissions")
      )
      .finally(() => setLoading(false));
  }, [open, user, logout, navigate]);

  if (!open || !user) return null;

  /* ================= HELPERS ================= */
  const has = (resource, action) =>
    permissions[resource]?.includes(action);

  const toggle = (resource, action) => {
    setPermissions((prev) => {
      const set = new Set(prev[resource] || []);

      if (set.has(action)) {
        set.delete(action);
      } else {
        set.add(action);
      }

      // 🔐 HARD ENFORCEMENT
      if (
        set.has("create") ||
        set.has("update") ||
        set.has("delete")
      ) {
        set.add("read");
      }

      return {
        ...prev,
        [resource]: Array.from(set),
      };
    });
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    setSaving(true);
    setError("");

    const payload = [];

    Object.entries(permissions).forEach(([resource, actions]) => {
      actions.forEach((action) => {
        payload.push({ resource, action });
      });
    });

    try {
      const res = await apiFetch(
        `/users/${user.uid}/permissions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        logout,
        navigate
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Save failed");
      }

      onClose();
    } catch (err) {
      setError(err.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="perm-overlay">
      <div className="perm-modal">
        <div className="perm-header">
          <h3>User Permissions</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="perm-subtitle">
          UID: <strong>{user.uid}</strong> &nbsp;|&nbsp; Role:{" "}
          <strong>{user.usertype}</strong>
        </div>

        {loading ? (
          <div className="perm-loading">Loading permissions…</div>
        ) : (
          <table className="perm-table">
            <thead>
              <tr>
                <th>Resource</th>
                {ACTIONS.map((a) => (
                  <th key={a}>{a.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {RESOURCES.map((r) => (
                <tr key={r}>
                  <td className="resource">{r}</td>
                  {ACTIONS.map((a) => (
                    <td key={a}>
                      <input
                        type="checkbox"
                        checked={has(r, a)}
                        disabled={
                          a === "read" &&
                          (has(r, "create") ||
                            has(r, "update") ||
                            has(r, "delete"))
                        }
                        onChange={() => toggle(r, a)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {error && <div className="perm-error">{error}</div>}

        <div className="perm-footer">
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            className="cancel-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}